import type { SessionResponse, Unternehmen } from "@kundeninfo/api-client";

export type Benutzer = NonNullable<SessionResponse["user"]>;

export type Sitzung = {
  user: Benutzer;
  companies: Unternehmen[];
  /** Das Aircall-Unternehmen aus der Sitzung (Mandantentrennung R10). */
  aircallCompanyId?: string | null;
};

/**
 * Ob dieser Benutzer eine fremde Bearbeitungssperre übernehmen darf: LEAD und ADMIN
 * (Leitungsfunktionen), EDITOR nicht. Entscheidet nur, ob die Oberfläche die Übernahme
 * ANBIETET — fail closed liegt beim Server, der Unberechtigte mit 403 abweist.
 */
export function darfSperreUebernehmen(user: Pick<Benutzer, "role">): boolean {
  return user.role === "LEAD" || user.role === "ADMIN";
}

/**
 * Ob dieser Benutzer Lieferantendaten sieht (Entscheid 496): LEAD und ADMIN. Entscheidet nur, was die Oberfläche
 * anbietet — Reiter, Bereiche, Navigation, Kacheln (Lieferanten Aufgabe 22); den Riegel trägt der Server
 * (`requireSupplierView`, 403 `LIEFERANTENDATEN_GESPERRT`). Der Spiegel von `seesSupplierData` der Domäne, die das Web
 * nicht importiert.
 */
export function seesSupplierData(role: Benutzer["role"] | undefined): boolean {
  return role === "LEAD" || role === "ADMIN";
}

/**
 * Das HEIMATunternehmen — die feste Zuordnung des Benutzers. Logo und Farbe im Kopf bestimmt es nicht (mehr): Die folgen
 * dem Arbeitsunternehmen (`arbeitsunternehmen` unten); dort ist die Heimat nur Rückfall ohne Wahl des Tabs und ohne
 * gespeicherte Vorgabe. Ohne Zuordnung (homeCompanyId fehlt oder verweist auf kein sichtbares Unternehmen) `null`.
 */
export function homeCompany(sitzung: Sitzung): Unternehmen | null {
  const id = sitzung.user.homeCompanyId;
  if (id === undefined || id === null) return null;
  return sitzung.companies.find((c) => c.id === id) ?? null;
}

/**
 * Das AKTIVE UNTERNEHMEN dieses Fensters (Spec Mandantentrennung, R1/R16; in der Oberfläche
 * weiter „Arbeitsunternehmen"): die Wahl des Fensters, sonst die gespeicherte Vorgabe, sonst die
 * Heimat, sonst ME, sonst das erste sichtbare. Dieselbe Kette wie `aktivesUnternehmen` in der
 * Domäne (der Server geht sie für den Kopf jeder Anfrage) — eine Wahl, die auf kein sichtbares
 * Unternehmen zeigt, zählt wie keine.
 */
export function arbeitsunternehmen(sitzung: Sitzung, fensterWahl: string | null = null): Unternehmen | null {
  const finde = (id: string | null | undefined): Unternehmen | undefined =>
    id === null || id === undefined ? undefined : sitzung.companies.find((c) => c.id === id);
  return finde(fensterWahl) ?? finde(sitzung.user.workCompanyId) ?? homeCompany(sitzung)
    ?? sitzung.companies.find((c) => c.shortCode.toUpperCase() === "ME") ?? sitzung.companies[0] ?? null;
}

/** Der Schlüssel der FENSTERWAHL im sessionStorage (R3) — er gehört genau einem Tab; ein duplizierter Tab erbt ihn
 *  (der Browser kopiert den sessionStorage), ein NEU geöffneter beginnt ohne. */
const FENSTER_SCHLUESSEL = "ki.aktivesUnternehmen";

/** Die Wahl dieses Fensters; ohne Speicher (privates Fenster, gesperrt) `null` — dann gilt die Vorgabe. */
export function leseFensterUnternehmen(): string | null {
  try { return window.sessionStorage.getItem(FENSTER_SCHLUESSEL); } catch { return null; }
}

export function merkeFensterUnternehmen(id: string): void {
  try { window.sessionStorage.setItem(FENSTER_SCHLUESSEL, id); } catch { /* ohne Speicher gilt die Vorgabe */ }
}
export function vergissFensterUnternehmen(): void {
  try { window.sessionStorage.removeItem(FENSTER_SCHLUESSEL); } catch { /* nichts zu tun */ }
}

/**
 * Der Tab MERKT SICH beim ersten Sitzungsstand sein Unternehmen (Dirk 391, Spec R3): Die abgeleitete Wahl der Kette
 * kommt in den sessionStorage, wenn sie dort noch nicht steht. Danach bleibt der Tab dabei, bis in ihm selbst
 * gewechselt wird — ein Wechsel in einem anderen Tab ändert nur die gespeicherte Vorgabe (für neue Tabs), nicht
 * diesen, auch nicht nach dem Neuladen. Liefert die Wahl des Tabs; ohne sichtbares Unternehmen bleibt alles, wie
 * es ist. Ohne Speicher gilt die Wahl bis zum Neuladen.
 */
export function halteFensterUnternehmen(sitzung: Sitzung, fensterWahl: string | null): string | null {
  const id = arbeitsunternehmen(sitzung, fensterWahl)?.id ?? null;
  if (id === null) return fensterWahl;
  if (id !== fensterWahl) merkeFensterUnternehmen(id);
  return id;
}

/** Die KUNDENSUCHE über einen Wechsel hinweg (Spec Mandantentrennung, R22): vor dem Neuladen abgelegt,
 *  beim Start gelesen und danach gelöscht — ein späteres Neuladen bringt keine alte Suche zurück. */
const SUCHE_SCHLUESSEL = "ki.kundensuche";

export function merkeKundensuche(text: string): void {
  try { window.sessionStorage.setItem(SUCHE_SCHLUESSEL, text); } catch { /* ohne Speicher geht die Suche verloren */ }
}
export function leseKundensuche(): string | null {
  try { return window.sessionStorage.getItem(SUCHE_SCHLUESSEL); } catch { return null; }
}
export function vergissKundensuche(): void {
  try { window.sessionStorage.removeItem(SUCHE_SCHLUESSEL); } catch { /* nichts zu tun */ }
}

export type AircallSicht = "zeigen" | "hinweis" | "verbergen";

/**
 * Wie Aircall erscheint (Spec Mandantentrennung, E365): im Aircall-Unternehmen für alle, für ADMIN
 * überall mit Hinweis auf die Anbindung, sonst gar nicht. Eine Sicht — die Aircall-Wege des Servers
 * prüfen unverändert (E357).
 */
export function aircallSicht(
  istAdmin: boolean, aktivId: string | null, aircallCompanyId: string | null,
): AircallSicht {
  if (aircallCompanyId !== null && aktivId === aircallCompanyId) return "zeigen";
  return istAdmin ? "hinweis" : "verbergen";
}

/** Der Hinweis auf die Aircall-Anbindung (E365) — EINE Quelle für Schnittstellen und Kundenakte. */
export function aircallHinweis(
  companies: readonly Unternehmen[], aircallCompanyId: string | null,
): string {
  const name = companies.find((c) => c.id === aircallCompanyId)?.name;
  return name === undefined ? "Aircall ist keinem aktiven Unternehmen zugeordnet."
    : `Aircall ist an ${name} angebunden.`;
}
